/**
 * Where this client sends its API requests, decided at RUN time.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 * ────────────────────────────────────────────────────────────────────────
 *
 * `process.env.VITE_API_URL` is a build-time textual substitution: Vite's
 * `define` replaces it during `npm run build` (exactly as CRA's webpack
 * DefinePlugin used to) and it cannot change afterwards by any means. That is
 * fine for a web deployment, where the bundle is rebuilt whenever the server
 * moves, and fine for the desktop app, where the server shares the page's own
 * origin at /api.
 *
 * It is not fine for an app-store binary. A shipped .ipa or .aab is frozen at
 * the commit it was built from and is expected to live for months; pinning it
 * permanently to one hostname means a server move bricks every installed copy,
 * and a field laptop's LAN address cannot be known at build time at all.
 *
 * So the base URL is resolved at run time, in this order:
 *
 *   1. an explicit choice the operator saved (localStorage)
 *   2. the build-time VITE_API_URL (or the older REACT_APP_API_URL), when set
 *   3. same-origin /api, which is what the desktop server serves
 *
 * (2) keeps every existing deployment behaving exactly as before: a web build
 * made with either name still uses it, and nothing needs reconfiguring.
 *
 * A base that names a loopback host (127.x, localhost, [::1]) is skipped in
 * (1) and (2) whenever this page was NOT itself opened on loopback. Loopback
 * means "this device": on the laptop that is the server, on a phone that
 * opened the laptop's LAN address it is the phone, where nothing answers. A
 * choice saved while testing on the laptop, or a bundle built with a loopback
 * VITE_API_URL, would otherwise pin every phone to itself, and every request
 * would fail as "no connection" with nothing to say why. The phone then uses
 * same-origin /api: under the Vite dev server that is its proxy, which
 * follows a loopback VITE_API_URL to the same backend the laptop talks to
 * (vite.config.js devProxy), never a different one.
 */

import { IS_NATIVE } from './platform';

const OVERRIDE_KEY = 'mb_api_base';

/**
 * True when this client has nowhere sensible to send requests yet.
 *
 * A relative base ("/api") means "the server that served this page". In a
 * browser and in the desktop app that IS the server. Inside a Capacitor shell
 * the page was served by the app bundle itself, so "/api" points at nothing --
 * and this repository holds no cloud API address to compile in as a default.
 * A native build with no absolute API base compiled in must therefore ask.
 */
export function needsServerChoice() {
  if (hasOverride()) return false;
  const built = normaliseBase(BUILD_TIME_BASE);
  if (built && !built.startsWith('/') && reachableFromHere(built)) return false;
  return IS_NATIVE;
}

/**
 * The compiled-in default, or '' when the bundle was built without one.
 *
 * VITE_API_URL is the current name; REACT_APP_API_URL is the old one, still
 * read so a builder not yet updated keeps producing a working bundle for one
 * release. Both are injected by `define` in vite.config.js, so this is a
 * build-time substitution in the app and an ordinary process.env read under
 * the test runner.
 */
export const BUILD_TIME_BASE =
  process.env.VITE_API_URL || process.env.REACT_APP_API_URL || '';

/**
 * Same-origin fallback. The desktop server mounts the API at /api, and that
 * prefix is mandatory rather than stylistic: eleven SPA routes collide
 * character-for-character with eleven API paths.
 */
export const SAME_ORIGIN_BASE = '/api';

function read() {
  try {
    return localStorage.getItem(OVERRIDE_KEY) || '';
  } catch (e) {
    // Private browsing, disabled storage, or a WebView that has evicted it.
    // Falling through to the build-time default is the right degraded state.
    return '';
  }
}

/** Trailing slashes would produce `https://host//auth/login`. */
export function normaliseBase(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed.replace(/\/+$/, '');
}

/**
 * A base URL must be an absolute http(s) URL or a root-relative path. Anything
 * else -- a bare hostname, a `javascript:` scheme, a typo -- is rejected rather
 * than silently producing requests that go nowhere or somewhere unintended.
 */
export function isValidBase(value) {
  const base = normaliseBase(value);
  if (!base) return false;
  if (base.startsWith('/')) return true;
  try {
    const url = new URL(base);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (e) {
    return false;
  }
}

const LOOPBACK_HOST = /^(?:127(?:\.\d{1,3}){3}|localhost|\[::1\])$/i;

/** 127.0.0.0/8, localhost or [::1] -- an address that means "this device". */
export function isLoopbackHost(hostname) {
  return LOOPBACK_HOST.test(String(hostname || '').trim().replace(/\.$/, ''));
}

/** True when an absolute base names a loopback host. A relative one never does. */
export function pointsAtLoopback(value) {
  const base = normaliseBase(value);
  if (!base || base.startsWith('/')) return false;
  try {
    return isLoopbackHost(new URL(base).hostname);
  } catch (e) {
    return false;
  }
}

/** Whether this page was opened on loopback: the laptop itself, or a
 *  Capacitor shell (https://localhost). Unknown counts as loopback, so a
 *  base is never discarded on a guess. */
export function pageIsLoopback() {
  try {
    const host = window.location.hostname;
    return !host || isLoopbackHost(host);
  } catch (e) {
    return true;
  }
}

/** False for a loopback base on a page that is not on loopback: from here it
 *  would name this device, not the server. */
export function reachableFromHere(value) {
  return !pointsAtLoopback(value) || pageIsLoopback();
}

export function getApiBase() {
  const override = normaliseBase(read());
  if (override && isValidBase(override) && reachableFromHere(override)) return override;
  const built = normaliseBase(BUILD_TIME_BASE);
  if (built && reachableFromHere(built)) return built;
  return SAME_ORIGIN_BASE;
}

/** True when the operator has pointed this client somewhere by hand -- and
 *  that choice is in force here (getApiBase skips a loopback one on a phone). */
export function hasOverride() {
  const override = normaliseBase(read());
  return Boolean(override) && isValidBase(override) && reachableFromHere(override);
}

/**
 * Save (or, with a falsy value, clear) the override.
 * Returns false without saving when the value is not a usable base URL.
 */
export function setApiBase(value) {
  const base = normaliseBase(value);
  try {
    if (!base) {
      localStorage.removeItem(OVERRIDE_KEY);
      return true;
    }
    if (!isValidBase(base)) return false;
    localStorage.setItem(OVERRIDE_KEY, base);
    return true;
  } catch (e) {
    return false;
  }
}

export function clearApiBase() {
  return setApiBase('');
}

/** Absolute URL for one path, for the callers that do not go through axios. */
export function apiUrl(path) {
  const base = getApiBase();
  const suffix = String(path || '');
  return `${base}${suffix.startsWith('/') ? '' : '/'}${suffix}`;
}
